import { useReducer, Dispatch } from 'react';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { mapRelayFacets } from './mapRelayFacets';
import { isPhotoFacetType } from './isPhotoFacetType';
import { PAGE_SIZE, FACET_TYPES } from '../constants';

import {
    ModifyFacetPayload,
    RelayFacets,
    RelayVariables,
    FacetSearchPayload,
    FacetSearchPayloadValues,
} from '../types';

export const CHANGE_PAGE = 'CHANGE_PAGE' as const;
export const CHANGE_FACET = 'CHANGE_FACET' as const;
export const DELETE_FACET = 'DELETE_FACET' as const;
export const SEARCH_PHOTOS = 'SEARCH_PHOTOS' as const;
export const RESET_SEARCH = 'RESET_SEARCH' as const;
export const SET_FACETS = 'SET_FACETS' as const;
export const ADD_RELATED_DESIGN_FACET = 'ADD_RELATED_DESIGN_FACET' as const;

export type FacetFilterAction =
    | typeof CHANGE_PAGE
    | typeof CHANGE_FACET
    | typeof DELETE_FACET
    | typeof SEARCH_PHOTOS
    | typeof RESET_SEARCH
    | typeof SET_FACETS
    | typeof ADD_RELATED_DESIGN_FACET;

export type FacetFilter = {
    first?: number;
} & FacetSearchPayload;

type FacetFilterActionPayload =
    | { type: typeof SET_FACETS; facets: FacetFilter }
    | { type: typeof CHANGE_PAGE; page: number }
    | { type: typeof RESET_SEARCH };

type ModifySingleFacetPayload = {
    facetFilter: FacetFilter;
    facets?: RelayFacets;
    payload?: Partial<ModifyFacetPayload>;
};

export function modifyFacet({
    facetFilter,
    facets,
    payload,
}: ModifySingleFacetPayload): FacetFilter {
    const { facetType, checked, value } = payload || {};
    const updatedFacetPayload: FacetFilter = {
        ...facetFilter,
        page: 1,
    };
    if (isPhotoFacetType(facetType)) {
        let updatedFacets: FacetSearchPayloadValues = [];
        const facetsByType = facetFilter[facetType];
        if (checked) {
            // Add checked facet to facet type array
            const variables = {
                [facetType]: [...facetsByType, { value }]
                    .map(f => f.value ?? null)
                    .filter(filterNulls),
            };
            const mappedFacets = mapRelayFacets({ variables, facets });
            updatedFacets = mappedFacets[facetType];
        } else {
            // Remove unchecked facet from facet type array
            updatedFacets = facetsByType.filter(f => f.value !== value);
        }
        updatedFacetPayload[facetType] = updatedFacets;
    }

    return updatedFacetPayload;
}

export const addRelatedDesignFacet = ({
    facetFilter,
    payload,
}: ModifySingleFacetPayload): FacetFilter => {
    const { facetType, value, label } = payload || {};
    const updatedFacetPayload: FacetFilter = {
        ...facetFilter,
        page: 1,
    };
    if (isPhotoFacetType(facetType)) {
        updatedFacetPayload[facetType] = [{ label: label || '', value: value || '' }];
    }

    return updatedFacetPayload;
};

type ModifyFacetsPayload = {
    action: FacetFilterAction;
} & ModifySingleFacetPayload;

export function modifyFacets({
    facetFilter,
    action,
    facets,
    payload,
}: ModifyFacetsPayload): FacetFilter {
    switch (action) {
        case SET_FACETS:
            return { ...facetFilter, ...payload };
        case CHANGE_PAGE:
            if (typeof payload?.page === 'number') {
                return { ...facetFilter, page: payload.page };
            }
        case CHANGE_FACET: {
            return modifyFacet({ facetFilter, facets, payload });
        }
        case ADD_RELATED_DESIGN_FACET: {
            return addRelatedDesignFacet({ facetFilter, facets, payload });
        }
        case DELETE_FACET: {
            const facetType = payload?.facetType;
            if (facetType === FACET_TYPES.SEARCH) {
                return { ...facetFilter, page: 1, searchTerm: '' };
            }
            return modifyFacet({ facetFilter, facets, payload });
        }
        case SEARCH_PHOTOS:
            return { ...facetFilter, page: 1, searchTerm: payload?.searchTerm || '' };
        case RESET_SEARCH:
            return { page: 1, searchTerm: '', style: [], room: [], project: [], creator: [] };
        default:
            return facetFilter;
    }
}

function facetFilterReducer(state: FacetFilter, action: FacetFilterActionPayload): FacetFilter {
    switch (action.type) {
        case SET_FACETS:
            return { ...state, ...action.facets };
        case CHANGE_PAGE:
            return { ...state, page: action.page };
        default:
            return state;
    }
}

export type UseFacetReducer = {
    facetFilter: FacetFilter;
    dispatchFacetFilter: Dispatch<FacetFilterActionPayload>;
};

type UseFacetReducerPayload = {
    facets?: RelayFacets;
    relayVariables: RelayVariables;
};

export function useFacetReducer({
    facets,
    relayVariables,
}: UseFacetReducerPayload): UseFacetReducer {
    const [facetFilter, dispatchFacetFilter] = useReducer(facetFilterReducer, {
        page: relayVariables.page ?? 1,
        searchTerm: relayVariables.searchTerm || '',
        first: relayVariables.first ?? PAGE_SIZE,
        ...mapRelayFacets({ facets, variables: relayVariables }),
    });

    return {
        facetFilter,
        dispatchFacetFilter,
    };
}
